//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025,2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import axios from './axios';
import { useConfig } from '../Config';
import Q from 'q';
import async from 'async';
import { User } from 'oidc-client-ts';
import { ResponseType } from 'axios';
import { getSafeToken } from './securityUtils';

import SocketService, {
  StreamingEvent,
  ApprovalRequest,
  ClarificationRequest,
  ChatCompleteEvent,
} from '../components/Loops/AI/SocketService';
import type {
  BackendTurn,
  ConversationTurnsPage,
  ConversationTurnsResponse,
} from '../types/conversationHistory';

type JSONPrimitive = string | number | boolean;
type JSONObject = { [key: string]: JSONValue };
type JSONArray = JSONValue[];
type JSONValue = JSONPrimitive | JSONObject | JSONArray | undefined;

interface QueueOptions {
  method: string;
  url: string;
  body: object;
  headers: { [key: string]: string };
  queryParameters: { [key: string]: string };
  form: FormData;
  deferred: Q.Deferred<any>;
  responseType: ResponseType;
}

/**
 * Prepares parameters to be sent as part of the URL, they must be encoded as a UTF-8 string.
 * Parameters are joined together using '&' and a '=' separates parameter names from their values
 *
 * @param {object} parameters - contains the parameters and their values
 *
 * @returns a conversion of a list of parameters into a UTF-8 string that can be tacked onto the end of a URL sent in an HTTP request
 */
function serializeQueryParams(parameters: { [key: string]: string }) {
  const str = [];
  for (const p in parameters) {
    if (parameters.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(parameters[p]));
    }
  }
  return str.join('&');
}

export interface ChatMessage {
  sender: 'user' | 'ai';
  message: string;
}

export interface ChatResponse {
  success: boolean;
  message: string;
  // Backend returns conversation_id as the session identifier and
  // base_conversation_id as the stable conversation id.
  conversation_id: string;
  base_conversation_id?: string;
  task_id?: string;
  error?: string;
}

export interface ValidationResponse {
  success: boolean;
  message: string;
  details?: any;
  error?: string;
}

export interface TriggerIngestionServerEntry {
  job_id: string;
  server_id: string;
  server_name: string;
  target_key: string;
  status: string;
}

export interface TriggerIngestionFailedEntry {
  server_id: string;
  server_name: string;
  message: string;
}

export interface TriggerIngestionDetails {
  teamspace_id: string;
  total_servers: number;
  started_servers: TriggerIngestionServerEntry[];
  reused_servers: TriggerIngestionServerEntry[];
  failed_to_queue: TriggerIngestionFailedEntry[];
}

export interface TriggerIngestionResponse {
  success: boolean;
  message: string;
  status: string;
  details?: TriggerIngestionDetails;
  error?: string;
}

// getConversationTurns's defensive page cap — see its own comment.
const CONVERSATION_TURNS_MAX_PAGES = 500;

// Longest a single tool-output download may take. Stored outputs can be large (up to 200 MiB).
const TOOL_OUTPUT_DOWNLOAD_TIMEOUT_MS = 10 * 60 * 1000;

class AiDataService {
  static instance: AiDataService;
  private readonly queueAPICalls: async.QueueObject<unknown>;
  private readonly getIsAuthenticated: () => boolean;
  private readonly analyticsAIUrl: string | undefined;
  private readonly analyticsAISocketUrl: string | undefined;
  private readonly getUserContext: () => User;
  constructor(getUserContext: () => User, getIsAuthenticated: () => boolean) {
    this.getUserContext = getUserContext;
    const { services } = useConfig();
    if (!AiDataService.instance) {
      AiDataService.instance = this;
    }

    this.getIsAuthenticated = getIsAuthenticated;
    this.analyticsAIUrl = services?.analyticsai?.url;
    this.analyticsAISocketUrl = services?.analyticsai?.socketUrl;
    this.queueAPICalls = async.queue((options: QueueOptions, callback) => {
      AiDataService.instance._request(
        options.method,
        options.url,
        options.body,
        options.headers,
        options.queryParameters,
        options.form,
        options.deferred,
        function () {
          callback();
        },
        getUserContext,
        getIsAuthenticated,
        services?.analyticsai?.url,
        options.responseType,
      );
    }, 3);
  }

  /**
   * The main request method that all API method will eventually call.
   *
   * @param method - http method
   * @param url - url to do request
   * @param body - body parameters / object
   * @param headers - header parameters
   * @param queryParameters - querystring parameters
   * @param form - form data object
   * @param deferred - promise object
   * @param completeJob - Call back to queue to communicate that job is done and the queue can pick up new job.
   * @param getUserContext - Function that returns user object to get access token from
   * @param getIsAuthenticated - Function that returns boolean value determining if user is currently authenticated
   * @param baseUrl - Request base url
   * @param responseType - Expected type of the response to the HTTP request, defaults to JSON
   */
  public _request(
    method: string,
    url: string,
    body: object,
    headers: { [key: string]: string },
    queryParameters: { [key: string]: string },
    form: FormData | object,
    deferred: Q.Deferred<any>,
    completeJob: () => void,
    getUserContext: () => User,
    getIsAuthenticated: () => boolean,
    baseUrl: string | undefined,
    responseType: ResponseType = 'json',
    timeoutMs?: number,
  ) {
    const user = getUserContext();
    const isAuthenticated = getIsAuthenticated();

    const queryParams =
      queryParameters && Object.keys(queryParameters).length
        ? serializeQueryParams(queryParameters)
        : null;
    const urlWithParams = url + (queryParams ? '?' + queryParams : '');
    // if user is logged in
    headers['Authorization'] = 'Bearer ' + getSafeToken(user.access_token);
    // GET requests carry no body, so Content-Type is dead weight: it adds header
    // bytes to every read and can trigger an unnecessary CORS preflight on
    // cross-origin calls. Strip it centrally so call sites don't have to remember.
    if (method.toUpperCase() === 'GET') {
      delete headers['Content-Type'];
    }
    if (!isAuthenticated) {
      deferred.reject('User is not logged in.');
      completeJob();
      return;
    }
    if (body && !Object.keys(body).length && !Array.isArray(body)) {
      body = {};
    }
    axios({
      method,
      url: urlWithParams,
      baseURL: baseUrl,
      headers,
      responseType: responseType,
      ...(timeoutMs ? { timeout: timeoutMs } : {}),
      data:
        method === 'POST' && form && Object.keys(form).length > 0 ? form : body,
    })
      .then((response) => {
        deferred.resolve(response.data || response);
        completeJob();
      })
      .catch((error) => {
        deferred.reject(error.response);
        completeJob();
      });
  }

  public request<T>(
    method: string,
    url: string,
    body: object,
    headers: JSONValue,
    queryParameters: JSONValue,
    form: JSONValue,
    deferred: Q.Deferred<T>,
    responseType = 'json',
  ) {
    this.queueAPICalls.push({
      method: method,
      url: url,
      body: body,
      headers: headers,
      queryParameters: queryParameters,
      form: form,
      deferred: deferred,
      responseType: responseType,
    });
  }

  /**
   * Fetches MCP tools available to the requesting user in the given teamspace.
   * Backend filters by enabled/connected state via the effective_catalog.
   *
   * @param teamspaceId  Required. Current teamspace id from useTeamspaceContext().
   * @returns Promise resolving to array of {name, description} objects.
   */
  fetchTools(teamspaceId: string) {
    const deferred = Q.defer<{
      tools: { name: string; description: string }[];
      total: number;
    }>();

    if (!teamspaceId) {
      deferred.reject({
        data: {
          error: 'Missing teamspace_id',
          message: 'teamspaceId is required',
        },
      });
      return deferred.promise;
    }

    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      '/api/tools',
      { teamspace_id: teamspaceId },
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Validates an AI model integration configuration
   *
   * @param integrationData - Complete integration data including all configuration
   * @returns Promise with ValidationResponse object
   */
  validateModel(integrationData: any): Q.Promise<ValidationResponse> {
    const deferred = Q.defer<ValidationResponse>();

    const headers = {
      'Content-Type': 'application/json',
    };

    this.request(
      'POST',
      '/api/provider/validate',
      integrationData,
      headers,
      {},
      {},
      deferred,
    );

    return deferred.promise;
  }

  /**
   * Triggers data ingestion for a teamspace
   *
   * @param teamspaceId - ID of the teamspace to ingest data for
   * @param recreate - Whether to recreate the index (defaults to 'True')
   * @returns Promise with TriggerIngestionResponse object
   */
  triggerIngestion(
    teamspaceId: string,
    recreate: string = 'True',
  ): Q.Promise<TriggerIngestionResponse> {
    const deferred = Q.defer<TriggerIngestionResponse>();

    const body = {
      teamspace_id: teamspaceId,
      recreate: recreate,
    };

    const headers = {
      'Content-Type': 'application/json',
    };

    this.request('POST', '/api/ingest', body, headers, {}, {}, deferred);

    return deferred.promise;
  }

  /**
   * Sends a message to the AI chat service
   *
   * @param conversationId - ID of the conversation/chat loop
   * @param loopName - Name of the conversation loop or null
   * @param message - User message to send
   * @param teamspaceId - Optional ID of the teamspace
   * @param promptId - Optional ID of the prompt to use (defaults to 'ask-a-question')
   * @param specificTool - Optional specific tool to use for this request
   * @param onFocus - Optional array of focus areas for the AI
   * @param teamspaceName - Optional name of the teamspace
   * @param sessionId - Optional unique session ID for this chat session
   * @returns Promise with ChatResponse object
   */
  sendMessage(
    conversationId: string,
    loopName: string | null,
    message: string,
    teamspaceId?: string,
    promptId?: string,
    specificTool?: string,
    onFocus?: string[],
    teamspaceName?: string,
    taskId?: string,
    sessionId?: string,
    loopId?: string,
  ) {
    const deferred = Q.defer<ChatResponse>();

    const body: any = {
      conversation_id: conversationId,
      loop_name: loopName,
      loop_id: loopId || '',
      message: message,
      teamspace_id: teamspaceId || '',
      prompt_id: promptId || 'ask-a-question',
      taskId: taskId || '',
      session_id: sessionId || '',
    };

    if (specificTool && specificTool.trim() !== '') {
      body.specific_tool = specificTool;
    }

    if (onFocus && Array.isArray(onFocus) && onFocus.length > 0) {
      body.on_focus = onFocus;
    }

    if (teamspaceName) {
      body.teamspace_name = teamspaceName;
    }

    if (taskId) {
      body.task_id = taskId;
    }

    const headers = {
      'Content-Type': 'application/json',
    };

    this.request('POST', '/api/chat', body, headers, {}, {}, deferred);

    return deferred.promise;
  }

  public summarizeSearchResults(
    hits: any[],
    teamspaceId: string,
    userQuery: string,
    type: string,
  ): Q.Promise<string> {
    const deferred = Q.defer<string>();

    const body = {
      hits: hits,
      teamspace_id: teamspaceId,
      user_query: userQuery,
      type: type,
    };

    const headers = {
      'Content-Type': 'application/json',
    };

    this.request(
      'POST',
      '/api/summarize',
      body,
      headers,
      {},
      {},
      deferred,
      'text',
    );

    return deferred.promise;
  }

  /**
   * Fetch full message history for a given session.
   *
   * A session_id maps to either:
   *  - <conversation_id> (when no task_id is present), or
   *  - <conversation_id>::<task_id> (when task_id is present).
   */
  public getSessionMessages(sessionId: string): Q.Promise<any> {
    const deferred = Q.defer<any>();

    const headers = {
      'Content-Type': 'application/json',
    };

    const query = {
      session_id: sessionId,
    } as { [key: string]: string };

    this.request(
      'GET',
      '/api/messages',
      {},
      headers as any,
      query as any,
      {},
      deferred,
    );

    return deferred.promise;
  }

  /**
   * Fetch the turn-scoped record for a conversation (analyticsai-server PR
   * #409): per-turn assistant text, plan requests/decisions, tool approval
   * requests/decisions, and tool calls/results, each attributed to a
   * turn_id. See specs/006-persist-history-reconstruction/contracts/
   * history-data-service.md.
   *
   * Paginates internally (Q2 clarification: fetch every page before
   * resolving) — the caller always sees one resolved promise carrying the
   * complete, accumulated turn list, never a partial page. If any page's
   * request fails, the returned promise rejects rather than resolving with
   * a partial list; the caller is responsible for falling back (see
   * HistoryTab.tsx's independent catch around this call, FR-014).
   *
   * @param conversationId - ID of the conversation to fetch turns for
   * @returns Promise resolving to `{ turns: BackendTurn[] }`
   */
  public getConversationTurns(
    conversationId: string,
  ): Q.Promise<ConversationTurnsResponse> {
    const outer = Q.defer<ConversationTurnsResponse>();

    const headers = {
      'Content-Type': 'application/json',
    };

    const url = `/api/conversations/${encodeURIComponent(conversationId)}/turns`;

    const fetchPage = (
      cursor: string | null,
      accumulated: BackendTurn[],
      pageCount: number,
    ) => {
      // A cursor chain that never terminates (a backend bug, or a cursor
      // that cycles) would otherwise recurse forever, issuing unbounded
      // requests and never resolving or rejecting — the History tab would
      // hang open indefinitely on that conversation (found in review of
      // #989). A real conversation's turn count is bounded by how many
      // messages a user can plausibly send in one session; this cap is
      // far above that, so it only ever fires on a malformed response.
      if (pageCount >= CONVERSATION_TURNS_MAX_PAGES) {
        outer.reject(
          new Error(
            `getConversationTurns: exceeded ${CONVERSATION_TURNS_MAX_PAGES}-page limit for conversation ${conversationId} — aborting rather than fetching indefinitely`,
          ),
        );
        return;
      }

      const pageDeferred = Q.defer<ConversationTurnsPage>();

      const query: { [key: string]: string } = {};
      if (cursor) {
        query.cursor = cursor;
      }

      this.request(
        'GET',
        url,
        {},
        headers as any,
        query as any,
        {},
        pageDeferred,
      );

      pageDeferred.promise
        .then((page) => {
          const nextAccumulated = accumulated.concat(page?.turns ?? []);
          const nextCursor = page?.next_cursor ?? null;
          if (nextCursor) {
            fetchPage(nextCursor, nextAccumulated, pageCount + 1);
          } else {
            outer.resolve({ turns: nextAccumulated });
          }
        })
        .catch((error) => {
          outer.reject(error);
        });
    };

    fetchPage(null, [], 0);

    return outer.promise;
  }

  /**
   * Downloads the complete output of one tool call that was too large to show
   * (spec 007): `GET /api/conversations/<id>/tool-outputs/<tool_call_id>/download`.
   * Resolves with the file as a Blob. Rejects with the HTTP response, so the caller
   * can tell 404 ("no longer available") from 503 ("temporarily unavailable").
   *
   * @param conversationId - the chat's conversation (thread) id
   * @param toolCallId - the tool call whose output to download
   */
  public downloadToolOutput(
    conversationId: string,
    toolCallId: string,
  ): Q.Promise<Blob> {
    const deferred = Q.defer<Blob>();
    // Not through the shared queue (three at a time, shared with every other call of this
    // service): a very large or stalled download must not delay the chat's other requests.
    // It has its own time limit instead, so a stalled one ends with an error.
    this._request(
      'GET',
      `/api/conversations/${encodeURIComponent(conversationId)}/tool-outputs/${encodeURIComponent(toolCallId)}/download`,
      {},
      {},
      {},
      {},
      deferred,
      () => undefined,
      this.getUserContext,
      this.getIsAuthenticated,
      this.analyticsAIUrl,
      'blob',
      TOOL_OUTPUT_DOWNLOAD_TIMEOUT_MS,
    );
    return deferred.promise;
  }

  /**
   * Clears a chat conversation
   *
   * @param conversationId - ID of the conversation to clear
   * @param username - Username of the user clearing the chat
   * @param teamspaceId - ID of the teamspace
   * @returns Promise with the response data
   */
  public clearChat(
    conversationId: string,
    username: string,
    teamspaceId: string,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();

    const body = {
      session_id: conversationId,
      username: username,
      teamspace_id: teamspaceId,
    };

    const headers = {
      'Content-Type': 'application/json',
    };

    this.request(
      'POST',
      '/api/messages/clear',
      body,
      headers,
      {},
      {},
      deferred,
    );

    return deferred.promise;
  }

  /**
   * Get tasks for a loop within a teamspace
   *
   * @param teamspaceId - ID of the teamspace
   * @param loopId - ID of the loop
   * @returns Promise resolving to tasks list
   */
  public getLoopTasks(teamspaceId: string, loopId: string): Q.Promise<any> {
    const deferred = Q.defer<any>();

    const headers = {
      'Content-Type': 'application/json',
    };

    const url = `/teamspace/${encodeURIComponent(
      teamspaceId,
    )}/loop/${encodeURIComponent(loopId)}/tasks`;

    this.request('GET', url, {}, headers, {}, {}, deferred);

    return deferred.promise;
  }

  /**
   * Make a task favorite
   * @param teamspaceId - ID of the teamspace
   * @param loopId - ID of the loop
   * @param taskId - ID of the task
   * @returns Promise resolving to the updated task
   */
  public favouriteTask(
    teamspaceId: string,
    loopId: string,
    taskId: string,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();

    const headers = {
      'Content-Type': 'application/json',
    };
    const url = `/api/tasks/favorite`;
    const body = {
      teamspace_id: teamspaceId,
      loop_id: loopId,
      task_id: taskId,
    };

    this.request('POST', url, body, headers, {}, {}, deferred);
    return deferred.promise;
  }

  /**
   * Remove a task from favorites
   * @param teamspaceId - ID of the teamspace
   * @param loopId - ID of the loop
   * @param taskId - ID of the task
   * @returns Promise resolving to the updated task
   */
  public unfavouriteTask(
    teamspaceId: string,
    loopId: string,
    taskId: string,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();

    const headers = {
      'Content-Type': 'application/json',
    };
    const url = `/api/tasks/favorite`;
    const body = {
      teamspace_id: teamspaceId,
      loop_id: loopId,
      task_id: taskId,
    };

    this.request('DELETE', url, body, headers, {}, {}, deferred);
    return deferred.promise;
  }

  /**
   * Get favorite tasks for a teamspace and loop
   * @param teamspaceId - ID of the teamspace
   * @param loopId - ID of the loop
   * @returns Promise resolving to an array of favorite task IDs
   */
  public getFavoriteTasks(teamspaceId: string, loopId: string): Q.Promise<any> {
    const deferred = Q.defer<any>();

    const headers = {
      'Content-Type': 'application/json',
    };
    const url = `/api/tasks/favorite`;
    const query = {
      teamspace_id: teamspaceId,
      loop_id: loopId,
    } as { [key: string]: string };

    this.request('GET', url, {}, headers as any, query as any, {}, deferred);
    return deferred.promise;
  }

  /**
   * Connect to Socket.IO server for real-time multistep workflow
   * @param token - Authentication token
   * @returns Promise that resolves when connected
   */
  public async connectSocketIO(token: string): Promise<void> {
    if (!this.analyticsAISocketUrl) {
      throw new Error('Analytics AI URL not configured in services');
    }
    const socketUrl = this.analyticsAISocketUrl;
    const url = new URL(this.analyticsAISocketUrl);
    const path = url.pathname;
    // The live token is read again on every (re)connect attempt: the socket
    // outlives any access token, and the server refuses a stale one.
    return SocketService.connect(
      token,
      socketUrl,
      path,
      () => this.getUserContext()?.access_token,
    );
  }

  /**
   * Disconnect from Socket.IO server
   */
  public disconnectSocketIO(): void {
    SocketService.disconnect();
  }

  /**
   * Check if Socket.IO is connected
   */
  public isSocketIOConnected(): boolean {
    return SocketService.isConnected();
  }

  /**
   * Send a message using Socket.IO for multistep workflow with streaming
   * This method does NOT use the REST /api/chat endpoint
   *
   * @param conversationId - ID of the conversation/chat loop
   * @param loopName - Name of the conversation loop or null
   * @param message - User message to send
   * @param teamspaceId - ID of the teamspace
   * @param teamspaceName - Name of the teamspace
   * @param token - Authentication token
   * @param specificTool - Optional specific tool to use for this request
   * @param onFocus - Optional array of focus areas for the AI
   * @param sessionId - Optional unique session ID for this chat session
   */
  public sendMessageSocketIO(
    conversationId: string,
    loopName: string | null,
    message: string,
    teamspaceId: string,
    teamspaceName: string,
    token: string,
    specificTool?: string,
    onFocus?: string[],
    taskId?: string,
    sessionId?: string,
    loopId?: string,
    tenantId?: string,
    taskIcon?: string,
    taskName?: string,
  ): void {
    if (!SocketService.isConnected()) {
      throw new Error('Socket.IO not connected. Call connectSocketIO() first.');
    }
    const payload = {
      conversation_id: conversationId,
      loop_name: loopName,
      loop_id: loopId || '',
      message: message,
      teamspace_id: teamspaceId,
      teamspace_name: teamspaceName,
      specific_tool: specificTool || '',
      on_focus: onFocus || [],
      token: token,
      task_id: taskId || '',
      session_id: sessionId || '',
      tenant_id: tenantId || '',
      task_icon: taskIcon || '',
      task_name: taskName || '',
    };

    SocketService.sendChatRequest(payload);
  }

  /**
   * Send approval response for an action requiring approval
   * @param approved - Whether the action is approved
   * @param reason - Optional reason for approval/rejection
   */

  public sendApproval(approved: boolean, reason?: string): void {
    SocketService.sendApprovalResponse(approved, reason);
  }

  /**
   * Send clarification response when agent asks for more information
   * @param answer - The clarification answer
   * @param sessionId - Optional session ID to use for the clarification response
   */

  public sendClarification(answer: string, sessionId?: string): void {
    SocketService.sendClarificationResponse(answer, sessionId);
  }

  /**
   * Register callback for workflow events (streaming progress updates)
   * @param callback - Function to call when workflow event is received
   */

  public onWorkflowEvent(callback: (event: StreamingEvent) => void): void {
    SocketService.onWorkflowEvent(callback);
  }

  /**
   * Register callback for approval requests
   * @param callback - Function to call when approval is required
   */

  public onApprovalRequired(
    callback: (request: ApprovalRequest) => void,
  ): void {
    SocketService.onApprovalRequired(callback);
  }

  /**
   * Register callback for clarification requests
   * @param callback - Function to call when clarification is needed
   */
  public onClarificationNeeded(
    callback: (request: ClarificationRequest) => void,
  ): void {
    SocketService.onClarificationNeeded(callback);
  }
  /**
   * Register callback for chat completion
   * @param callback - Function to call when chat is complete
   */
  public onChatComplete(callback: (data: ChatCompleteEvent) => void): void {
    SocketService.onChatComplete(callback);
  }

  /**
   * Register callback for Socket.IO errors
   * @param callback - Function to call when error occurs
   */
  public onSocketError(
    callback: (error: { error: string; details?: string }) => void,
  ): void {
    SocketService.onError(callback);
  }

  /**
   * Remove all Socket.IO event listeners
   */
  public removeSocketListeners(): void {
    SocketService.removeAllListeners();
  }

  /**
   * Get loop-wise sessions for Chat UI
   * Uses real API in production/deployment, mock data in local development
   *
   * @param teamspaceId - ID of the teamspace
   * @param teamspaceName - Name of the teamspace
   * @param loopId - ID of the loop
   * @param page - Optional page number (default: 1)
   * @param limit - Optional limit per page (default: 20)
   * @param userOnly - Optional filter for user sessions (default: true)
   * @returns Promise resolving to sessions list
   */
  public getLoopSessions(
    teamspaceId: string,
    teamspaceName: string,
    loopId: string,
    page?: number,
    limit?: number,
    userOnly?: boolean,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();

    const headers = {
      'Content-Type': 'application/json',
    };

    const query: { [key: string]: string } = {
      teamspace_id: teamspaceId,
      teamspace_name: teamspaceName,
      loop_id: loopId,
    };

    if (page !== undefined) {
      query.page = page.toString();
    }
    if (limit !== undefined) {
      query.limit = limit.toString();
    }
    if (userOnly !== undefined) {
      query.user_only = userOnly.toString();
    }

    this.request(
      'GET',
      '/api/sessions',
      {},
      headers as any,
      query as any,
      {},
      deferred,
    );

    return deferred.promise;
  }

  /**
   * Get global sessions list for Admin/Analytics
   * Uses real API in production/deployment, mock data in local development
   *
   * @param teamspaceId - Optional ID of the teamspace
   * @param teamspaceName - Optional name of the teamspace
   * @param loopId - Optional ID of the loop
   * @param page - Optional page number (default: 1)
   * @param limit - Optional limit per page (default: 20)
   * @param userOnly - Optional filter for user sessions (default: true)
   * @returns Promise resolving to sessions list
   */
  public getGlobalSessions(
    teamspaceId?: string,
    teamspaceName?: string,
    loopId?: string,
    page?: number,
    limit?: number,
    userOnly?: boolean,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();

    const headers = {
      'Content-Type': 'application/json',
    };

    const query: { [key: string]: string } = {};

    if (teamspaceId) {
      query.teamspace_id = teamspaceId;
    }
    if (teamspaceName) {
      query.teamspace_name = teamspaceName;
    }
    if (loopId) {
      query.loop_id = loopId;
    }
    if (page !== undefined) {
      query.page = page.toString();
    }
    if (limit !== undefined) {
      query.limit = limit.toString();
    }
    if (userOnly !== undefined) {
      query.user_only = userOnly.toString();
    }

    this.request(
      'GET',
      '/api/list-sessions',
      {},
      headers as any,
      query as any,
      {},
      deferred,
    );

    return deferred.promise;
  }

  getModels(provider?: string): Q.Promise<{
    models: string[];
    provider_defaults: Record<string, string>;
    reasoning_models: string[];
  }> {
    const deferred = Q.defer<{
      models: string[];
      provider_defaults: Record<string, string>;
      reasoning_models: string[];
    }>();
    const queryParams = provider ? { provider } : {};
    this.request('GET', '/models', {}, {}, queryParams as any, {}, deferred);
    return deferred.promise;
  }

  /**
   * Whether LangSmith tracing is fully configured on this deployment (all 4
   * LANGSMITH_* env vars set on analyticsai-server). Used to gate the Loop
   * Genie thread-id badge. Resolves false on any failure — this is a
   * cosmetic capability check, not a critical path, so it fails closed
   * silently rather than surfacing an error to the user.
   */
  getTracingStatus(): Q.Promise<boolean> {
    const deferred = Q.defer<{ enabled: boolean }>();
    this.request('GET', '/api/config/tracing-status', {}, {}, {}, {}, deferred);
    return deferred.promise.then(
      (resp) => resp?.enabled === true,
      () => false,
    );
  }

  /**
   * Returns the catalogue models filtered to those the supplied API key can
   * actually access (authenticated POST /api/models).
   *
   * The backend performs a live call to the provider. If that call fails (bad
   * key, bad endpoint, network, etc.) the response has `ok: false` with an
   * `error` and empty model lists — there is NO fallback to the full catalogue,
   * so the caller should block the model field and surface the error. When no
   * key is supplied the unfiltered catalogue is returned with `ok: true`.
   */
  getModelsForKey(
    provider: string,
    apiKey: string,
    apiEndpoint?: string,
    teamspaceId?: string,
    integrationId?: string,
  ): Q.Promise<{
    ok: boolean;
    models: string[];
    provider_defaults: Record<string, string>;
    reasoning_models: string[];
    source?: string;
    error?: { type?: string; message: string };
  }> {
    const deferred = Q.defer<{
      ok: boolean;
      models: string[];
      provider_defaults: Record<string, string>;
      reasoning_models: string[];
      source?: string;
      error?: { type?: string; message: string };
    }>();
    const headers = { 'Content-Type': 'application/json' };
    this.request(
      'POST',
      '/api/models',
      { provider, apiKey, apiEndpoint, teamspaceId, integrationId },
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Create a new Slack connection for a teamspace.
   *
   * @param teamspaceId - ID of the teamspace
   * @param data - Object containing the connection name
   * @returns Promise resolving to connection details (connection_id, webhook_url, manifest, status)
   */
  createSlackConnection(
    teamspaceId: string,
    data: { name: string },
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();
    const headers = { 'Content-Type': 'application/json' };
    const url = `/teamspaces/${encodeURIComponent(teamspaceId)}/slack-connections`;
    this.request('POST', url, data, headers, {}, {}, deferred);
    return deferred.promise;
  }

  /**
   * Get all Slack connections for a teamspace.
   *
   * @param teamspaceId - ID of the teamspace
   * @returns Promise resolving to array of Slack connections
   */
  getSlackConnections(teamspaceId: string): Q.Promise<any> {
    const deferred = Q.defer<any>();
    const headers = { 'Content-Type': 'application/json' };
    const url = `/teamspaces/${encodeURIComponent(teamspaceId)}/slack-connections`;
    this.request('GET', url, {}, headers, {}, {}, deferred);
    return deferred.promise;
  }

  /**
   * Save the signing secret for a Slack connection.
   *
   * @param teamspaceId - ID of the teamspace
   * @param connectionId - ID of the Slack connection
   * @param signingSecret - The Slack app signing secret
   * @returns Promise resolving on success
   */
  saveSlackSigningSecret(
    teamspaceId: string,
    connectionId: string,
    signingSecret: string,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();
    const headers = { 'Content-Type': 'application/json' };
    const url = `/teamspaces/${encodeURIComponent(teamspaceId)}/slack-connections/${encodeURIComponent(connectionId)}/signing-secret`;
    this.request(
      'PUT',
      url,
      { signing_secret: signingSecret },
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Save the bot token for a Slack connection.
   *
   * @param teamspaceId - ID of the teamspace
   * @param connectionId - ID of the Slack connection
   * @param botToken - The Slack bot user OAuth token (xoxb-...)
   * @returns Promise resolving on success
   */
  saveSlackBotToken(
    teamspaceId: string,
    connectionId: string,
    botToken: string,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();
    const headers = { 'Content-Type': 'application/json' };
    const url = `/teamspaces/${encodeURIComponent(teamspaceId)}/slack-connections/${encodeURIComponent(connectionId)}/bot-token`;
    this.request(
      'PUT',
      url,
      { bot_token: botToken },
      headers,
      {},
      {},
      deferred,
    );
    return deferred.promise;
  }

  /**
   * Get a single Slack connection for a teamspace.
   *
   * @param teamspaceId - ID of the teamspace
   * @param connectionId - ID of the Slack connection
   * @returns Promise resolving to the Slack connection details
   */
  getSlackConnection(
    teamspaceId: string,
    connectionId: string,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();
    const headers = { 'Content-Type': 'application/json' };
    const url = `/teamspaces/${encodeURIComponent(teamspaceId)}/slack-connections/${encodeURIComponent(connectionId)}`;
    this.request('GET', url, {}, headers, {}, {}, deferred);
    return deferred.promise;
  }

  /**
   * Delete a Slack connection for a teamspace.
   *
   * @param teamspaceId - ID of the teamspace
   * @param connectionId - ID of the Slack connection to delete
   * @returns Promise resolving on success
   */
  deleteSlackConnection(
    teamspaceId: string,
    connectionId: string,
  ): Q.Promise<any> {
    const deferred = Q.defer<any>();
    const headers = { 'Content-Type': 'application/json' };
    const url = `/teamspaces/${encodeURIComponent(teamspaceId)}/slack-connections/${encodeURIComponent(connectionId)}`;
    this.request('DELETE', url, {}, headers, {}, {}, deferred);
    return deferred.promise;
  }
}

export default AiDataService;
